import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import type { ReactNode } from "react";

export default function Layout({ children }: { children: ReactNode }) {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Header */}
      <header
        className={`sticky top-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-slate-950/90 backdrop-blur-xl shadow-lg shadow-black/20 border-b border-slate-800/50"
            : "bg-slate-950/50 backdrop-blur-sm border-b border-transparent"
        }`}
      >
        {/* Subtle gradient border under header */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-indigo-500/20 to-transparent" />

        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-1.5 group">
            <span className="text-lg select-none transition-transform duration-200 group-hover:scale-110">
              ✨
            </span>
            <span className="text-xl font-extrabold bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
              StartupVibes
            </span>
          </Link>

          <nav className="flex items-center gap-2">
            <Link
              to="/search"
              className="btn-ghost text-sm flex items-center gap-1.5"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <span className="hidden sm:inline">Search</span>
            </Link>
            <Link
              to="/vcs"
              className="text-slate-400 hover:text-slate-200 transition-colors text-sm"
            >
              VCs
            </Link>
            <Link
              to="/submit"
              className="btn-primary text-sm flex items-center gap-1.5 !px-4 !py-2"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2.5}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              Submit
            </Link>
          </nav>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-6xl mx-auto px-4 py-8">{children}</main>

      {/* Footer */}
      <footer className="border-t border-slate-800/50 py-10 mt-16">
        <div className="max-w-6xl mx-auto px-4 text-center space-y-3">
          <div className="text-lg font-bold gradient-text inline-block">
            ✨ StartupVibes
          </div>
          <p className="text-slate-500 text-sm max-w-md mx-auto">
            The crowd-sourced vibe check for startups. Vote anonymously.
            Trust-weighted scores. No sign-up. No BS.
          </p>
          <p className="text-slate-600 text-xs">
            Not investment advice. Vibes only.
          </p>
        </div>
      </footer>
    </div>
  );
}
